import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { schoolAPI } from "../services/api";

const DEFAULT_RADIUS = 50;
const DEFAULT_LIMIT = 10;

const getOriginCoords = (school) => {
  if (!school) return null;
  const lat =
    school.latitude ??
    school.coordinates?.latitude ??
    school.location?.latitude;
  const lon =
    school.longitude ??
    school.coordinates?.longitude ??
    school.location?.longitude;
  if (lat === null || lat === undefined || lon === null || lon === undefined) {
    return null;
  }
  const latitude = Number(lat);
  const longitude = Number(lon);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
  return { latitude, longitude };
};

const formatDistance = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return `${num.toFixed(1)} mi away`;
};

const formatSatMath = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return `SAT Math Avg ${Math.round(num)}`;
};

const formatAcademicsGrade = (value) => {
  if (!value) return null;
  const trimmed = typeof value === "string" ? value.trim() : String(value);
  return trimmed ? `Academics ${trimmed}` : null;
};


const NearbyStemModal = ({
  isOpen,
  onClose,
  originSchool,
  onSelectSchool,
  onPrefetchSchools,
  prefetchedSchools,
  onCacheNearby,
}) => {
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const lastOriginKeyRef = React.useRef(null);

  const coords = useMemo(() => getOriginCoords(originSchool), [originSchool]);
  const originKey = useMemo(() => {
    if (!originSchool) return null;
    return (
      originSchool.id ||
      `${originSchool.school_name}-${originSchool.address_zipcode ?? originSchool.zip ?? ""}`
    );
  }, [originSchool]);

  useEffect(() => {
    if (!isOpen) return;
    if (originKey !== lastOriginKeyRef.current) {
      setSchools([]);
      setError(null);
      setLoading(false);
      setHasLoaded(false);
      lastOriginKeyRef.current = originKey;
    }
  }, [isOpen, originKey]);

  useEffect(() => {
    if (!isOpen) return;
    if (!coords) {
      setSchools([]);
      setError("Location unavailable for this school - can't find nearby STEM schools.");
      setLoading(false);
      setHasLoaded(true);
      return;
    }

    let isActive = true;
    const loadNearby = async () => {
      try {
        setLoading(true);
        setHasLoaded(false);
        setError(null);
        const response = await schoolAPI.getNearbyStemSchools({
          latitude: coords.latitude,
          longitude: coords.longitude,
          radius_miles: DEFAULT_RADIUS,
          limit: DEFAULT_LIMIT,
        });
        if (!isActive) return;
        const nextSchools = response?.data?.schools ?? [];
        const sortedSchools = [...nextSchools].sort(
          (a, b) => Number(a.distance_miles) - Number(b.distance_miles)
        );
        setSchools(sortedSchools);
        onCacheNearby?.(originSchool, nextSchools);
        setHasLoaded(true);
      } catch (err) {
        if (!isActive) return;
        setError(err.message || "Failed to load nearby schools.");
        setSchools([]);
        setHasLoaded(true);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadNearby();
    return () => {
      isActive = false;
    };
  }, [coords, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setHasLoaded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(prefetchedSchools) && prefetchedSchools.length > 0) {
      const sortedSchools = [...prefetchedSchools].sort(
        (a, b) => Number(a.distance_miles) - Number(b.distance_miles)
      );
      setSchools(sortedSchools);
      setError(null);
      setLoading(false);
      setHasLoaded(true);
    }
  }, [isOpen, prefetchedSchools]);

  useEffect(() => {
    if (!isOpen || schools.length === 0) return;
    onPrefetchSchools?.(schools);
  }, [isOpen, onPrefetchSchools, schools]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-[22px] md:text-[28px] font-unbounded text-[#0F172A] mt-1">
              Nearby STEM-focused schools
            </h2>
            <p className="text-sm text-[#575757] mt-1">
              Top options within {DEFAULT_RADIUS} miles.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900"
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {schools.length === 0 && !error && (!hasLoaded || loading) && (
            <div className="space-y-3 animate-pulse">
              {[0, 1, 2].map((index) => (
                <div
                  key={`nearby-skeleton-${index}`}
                  className="w-full rounded-2xl border border-[#F0F2F7] bg-white px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-40 rounded bg-[#E7EAF0]" />
                      <div className="h-3 w-56 rounded bg-[#EEF1F5]" />
                      <div className="h-3 w-64 rounded bg-[#EEF1F5]" />
                    </div>
                    <div className="h-3 w-16 rounded bg-[#F5C4A4]" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="py-10 text-center text-sm text-gray-500">
              {error}
            </div>
          )}

          {!loading && !error && hasLoaded && schools.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-500">
              No nearby STEM schools found within {DEFAULT_RADIUS} miles.
            </div>
          )}

          {!loading && !error && schools.length > 0 && (
            <div className="space-y-3">
              {schools.map((school) => (
                <button
                  key={school.id || `${school.school_name}-${school.zip}`}
                  type="button"
                  onClick={() => {
                    const normalizedSchool = {
                      ...school,
                      id: school.id,
                      school_name: school.school_name,
                      address_city: school.city,
                      address_state: school.state,
                      address_zipcode: school.zip,
                      latitude: school.latitude,
                      longitude: school.longitude,
                      Snapgrad_rating:
                        school.Snapgrad_rating ?? school.snapgrad_rating ?? null,
                    };
                    onSelectSchool?.(normalizedSchool);
                  }}
                  className="w-full rounded-2xl border border-[#F0F2F7] bg-white px-4 py-4 text-left transition hover:border-[#F07639] hover:bg-[#FFF8F4]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-[#111827]">
                        {school.school_name || "Unnamed school"}
                      </p>
                      <p className="text-sm text-[#6B7280]">
                        {[school.city, school.state, school.zip]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {(() => {
                        const satLabel = formatSatMath(school.sat_math_average);
                        const academicsLabel = formatAcademicsGrade(
                          school.grade_academics
                        );
                        const lineParts = [satLabel, academicsLabel].filter(Boolean);
                        if (lineParts.length === 0) return null;
                        return (
                          <p className="mt-2 text-xs text-[#9CA3AF]">
                            {lineParts.join(" \u2022 ")}
                          </p>
                        );
                      })()}
                    </div>
                    <span className="text-sm font-semibold text-[#F07639] shrink-0">
                      {formatDistance(school.distance_miles)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-t px-6 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">
            Disclaimer
          </p>
          <p className="mt-2 text-xs text-[#9CA3AF]">
            Nearby STEM-focused schools are ranked using estimated academic indicators (e.g., SAT Math and readiness signals) and filtered by approximate distance. Results may be incomplete or change as data updates—please verify STEM offerings directly with the school.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NearbyStemModal;
